import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import config from '../../config';
import { useAuth } from '../../AuthContext';
import { fetchSupplements } from '../../Utils';
import { color, r, radius } from '../../theme/tokens';
import { deriveUserType } from '../../utils/subscriptionState';
import { logAnalyticsEvent } from '../../utils/analytics';
import {
  CHECKER_MAX_MEDICATIONS,
  CHECKER_MAX_SUPPLEMENTS,
  getCheckerPicks,
  removeCheckerMedication,
  removeCheckerSupplement,
  subscribeCheckerPicks,
} from '../../utils/interactionCheckerStore';
import SEO from '../common/SEO';
import StackDisclaimer from '../common/StackDisclaimer';
import MedicationField from './MedicationField';
import SupplementField from './SupplementField';
import CheckerResults from './CheckerResults';
import CheckerTrialCard, { TRIAL_OUTCOME } from './CheckerTrialCard';
import { CheckingScreen, CouldntCheckScreen } from './CheckStates';
import useCheckerCheck, { CHECK_PHASE } from './useCheckerCheck';
import {
  CHECK,
  INTRO,
  NEEDS_BOTH,
  NEEDS_MEDICATION,
  NEEDS_SUPPLEMENT,
  PAGE_PATH,
  SEO_DESCRIPTION,
  SEO_TITLE,
  TITLE,
} from './copy';

// The supplement interaction checker page, `/supplement-interaction-checker`
// (STACK-587). Design: docs/supplement-interaction-checker.md §3; the UI is
// Tanu's v5.1, tickets/STACK-585/artifacts/interaction-checker-mock.html,
// with tickets/STACK-585/spec.md.
//
// Public and the same for everyone, signed in or not (Tanu, 2026-10-16). A
// visitor picks up to 3 medications and 3 supplements, taps Check, and sees
// each supplement's result. Under every result is the membership trial card
// (STACK-588), which a member does not see. With
// `config.FEATURES.INTERACTION_CHECKER` off the page keeps its heading, intro
// and disclaimer — it is indexed — offers the trial, and makes no request.

export const CANONICAL_URL = `https://my-stack.ai${PAGE_PATH}`;

/** What a disabled Check is waiting for, or null when it can check. */
export const checkHint = ({ medicationCount, supplementCount }) => {
  if (!medicationCount && !supplementCount) return NEEDS_BOTH;
  if (!supplementCount) return NEEDS_SUPPLEMENT;
  if (!medicationCount) return NEEDS_MEDICATION;
  return null;
};

const cardSx = {
  backgroundColor: color.paper,
  border: `${r(1)} solid ${color.line}`,
  borderRadius: radius.lg,
  px: r(16),
  py: r(20),
  display: 'flex',
  flexDirection: 'column',
  gap: r(24),
};

const CheckButton = ({ hint, onClick }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: r(10) }}>
    <Button
      variant="contained"
      disableElevation
      fullWidth
      disabled={Boolean(hint)}
      onClick={onClick}
      aria-describedby={hint ? 'checker-check-hint' : undefined}
      sx={{
        minHeight: r(52),
        borderRadius: radius.md,
        backgroundColor: color.accent,
        color: color.accentInk,
        fontSize: r(16),
        fontWeight: 700,
        textTransform: 'none',
        '&:hover': { backgroundColor: color.accentHover },
        // Disabled is the theme's disabled contained button (theme.js, with
        // !important), as on the Analyze form. That is a blue-grey, as v5.1
        // draws it, while the spec says never blue: the PR puts it to the
        // epic owner rather than choosing here.
      }}
    >
      {CHECK}
    </Button>
    {hint && (
      <Typography id="checker-check-hint" sx={{ m: 0, fontSize: r(13), color: color.inkSoft, textAlign: 'center' }}>
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * @param {Object} [props]
 * @param {Object} [props.checkOptions] - `{ retryDelayMs, maxRetries }`, tests only
 */
export default function InteractionCheckerPage({ checkOptions = {} } = {}) {
  const enabled = Boolean(config.FEATURES?.INTERACTION_CHECKER);
  const { currentUser, subscriptionStatus, isGrandfathered, loading } = useAuth();
  const [picks, setPicks] = useState(getCheckerPicks);
  const [catalog, setCatalog] = useState({ status: 'loading', list: [] });
  const viewed = useRef(false);

  const getIdToken = useCallback(
    async () => (currentUser ? currentUser.getIdToken() : null),
    [currentUser],
  );
  const { phase, rows, checked, run, retry } = useCheckerCheck({ getIdToken, ...checkOptions });

  // Every change, signing in's emptying included (AuthContext), arrives here.
  useEffect(() => subscribeCheckerPicks(setPicks), []);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    // A rejection or an empty list (fetchSupplements returns [] on error)
    // both mean we can't search supplements right now.
    fetchSupplements(config)
      .catch(() => [])
      .then((list) => {
        if (!live) return;
        const usable = Array.isArray(list) ? list.filter((s) => s && typeof s.key === 'string') : [];
        setCatalog(usable.length ? { status: 'ready', list: usable } : { status: 'failed', list: [] });
      });
    return () => { live = false; };
  }, [enabled]);

  const userType = deriveUserType({ isGrandfathered, subscriptionStatus, hasSubscriptionHistory: false });
  const member = Boolean(currentUser) && (userType === 'active' || userType === 'grandfathered');

  useEffect(() => {
    if (loading || viewed.current) return;
    viewed.current = true;
    logAnalyticsEvent('checker_page_viewed', { signed_in: Boolean(currentUser), member });
  }, [loading, currentUser, member]);

  // Results and couldn't-check replace the screen; start them at the top.
  useEffect(() => {
    if (phase === CHECK_PHASE.RESULTS || phase === CHECK_PHASE.COULDNT_CHECK) {
      try {
        window.scrollTo({ top: 0 });
      } catch (_) {
        // jsdom and old WebViews: not worth failing over.
      }
    }
  }, [phase]);

  const { medications, supplements } = picks;
  const hint = checkHint({ medicationCount: medications.length, supplementCount: supplements.length });
  const inForm = phase === CHECK_PHASE.FORM;
  // The trial card's button can do nothing for a member (design doc §11). Not
  // until auth has settled. `loading` clears on the subscription snapshot; a
  // grandfathered member's flag comes from the user doc, which can land just
  // after it, so on the flag-off page the card can show for that moment.
  const offerTrial = !loading && !member;

  return (
    <Box sx={{ backgroundColor: color.surface, display: 'flex', justifyContent: 'center', width: '100%' }}>
      <SEO title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalUrl={CANONICAL_URL} />
      <Box
        data-testid="interaction-checker-page"
        sx={{
          width: '100%',
          // Phone: v5.1's 390 frames, 16px gutters. Desktop (state 12): one
          // centred 760px column, under a centred heading. v5.1 draws no
          // desktop form; it sits in the same column.
          maxWidth: { xs: r(640), md: r(760 + 32) },
          px: r(16),
          pt: { xs: r(28), md: r(48) },
          pb: r(64),
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: r(16), md: r(20) },
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontSize: { xs: r(30), md: r(44) },
              fontWeight: 800,
              lineHeight: 1.12,
              letterSpacing: '-0.03em',
              color: color.evergreen,
              textWrap: 'balance',
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            {TITLE}
          </Typography>
          {/* The form's intro. v5.1's phone results frames drop it; the
              desktop results frame keeps it. */}
          <Typography
            sx={{
              m: 0,
              mt: r(8),
              fontSize: { xs: r(15), md: r(16) },
              textAlign: { xs: 'left', md: 'center' },
              color: color.inkSoft,
              display: inForm || !enabled ? 'block' : { xs: 'none', md: 'block' },
            }}
          >
            {INTRO}
          </Typography>
        </Box>

        {!enabled && <StackDisclaimer sx={{ mt: 0 }} />}
        {!enabled && offerTrial && <CheckerTrialCard outcome={TRIAL_OUTCOME.FLAG_OFF} signedIn={Boolean(currentUser)} />}

        {enabled && inForm && (
          <Box sx={cardSx} data-testid="checker-form">
            <MedicationField
              medications={medications}
              atCap={medications.length >= CHECKER_MAX_MEDICATIONS}
              signedIn={Boolean(currentUser)}
              onRemove={removeCheckerMedication}
            />
            <SupplementField
              supplements={supplements}
              catalog={catalog}
              atCap={supplements.length >= CHECKER_MAX_SUPPLEMENTS}
              signedIn={Boolean(currentUser)}
              onRemove={removeCheckerSupplement}
            />
            <StackDisclaimer sx={{ mt: 0 }} />
            <CheckButton hint={hint} onClick={() => run(medications, supplements)} />
          </Box>
        )}

        {enabled && phase === CHECK_PHASE.CHECKING && (
          <CheckingScreen medications={checked.medications} supplements={checked.supplements} />
        )}

        {enabled && phase === CHECK_PHASE.COULDNT_CHECK && (
          <CouldntCheckScreen medications={checked.medications} supplements={checked.supplements} onRetry={retry} />
        )}

        {enabled && phase === CHECK_PHASE.RESULTS && (
          <>
            <CheckerResults rows={rows} medications={checked.medications} />
            <StackDisclaimer sx={{ mt: 0 }} />
            {offerTrial && (
              <Box data-testid="checker-trial-slot">
                <CheckerTrialCard
                  outcome={rows.some((row) => row.level) ? TRIAL_OUTCOME.FLAGS : TRIAL_OUTCOME.CLEAN}
                  signedIn={Boolean(currentUser)}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
